import {useEffect, useState, useMemo} from 'react';
import ThemeProvider from '@/shared/theme/ThemeProvider';
import {observer} from 'mobx-react-lite';
import {toastStore} from '@/shared/stores/toastStore';

// import components
import {SdbDefaultTemplate} from '@sdb/ui/components/templates/SdbDefault/index';
import Snackbar from '@sdb/ui/components/atoms/Snackbar/index';
import Alert from '@sdb/ui/components/atoms/Alert/index';
import AnnounceKit from '@sdb/ui/components/atoms/AnnounceKit/index';
import useEnvironmentContext from '@sdb/ui/components/providers/Environment/useEnvironmentContext';
import {NpsClient} from '@sdb/ui/service-components/features/nps/index';

import '@sdb/ui/fonts';

import {store} from '@/store';
import {hashStringUtil} from '@sdb/ui/client-utils/crypto/hashString.util';
import useSwr from 'swr';
import {initI18n} from '@/shared/localize/initi18n';
import {UserDetailsSync} from '@/shared/update-user-details/UserDetailsSync';
import {getWhatsNewMenuData, getWhatsNewRailProps} from '@/app/whatsNewNav';
import useMediaQuery from '@sdb/ui/hooks/useMediaQuery/index';

// import stores
import announcekitStore from '@sdb/ui/client-store/announcekit.store';
import {messageSummaryStore} from '@/shared/stores/messageSummaryStore';
import DashboardFeatureDiscovery, {
    introFeatureDiscoveryStore,
} from '@/app/DashboardFeatureDiscovery';

// import hooks
import {useTranslation} from '@sdb/ui/translate/hooks/index';
import {useUserSettings} from '@/shared/hooks/useUserSettings.hook';
import {useNavigation} from '@/shared/hooks/useNavigation.hook';
import {useAcaAppLitElement} from '@/shared/hooks/useAcaAppLitElement.hook';
import {useBreadcrumbs} from '@/shared/hooks/useBreadcrumbs.hook';
import useInitializeTheme from '@/shared/hooks/useInitializeTheme.hook';
import getTaskItems from '@/shared/notifications/getTaskItems';
import useMinervaSettings from '@/shared/hooks/useMinervaSettings.hook';
import useTaskItemCounts from '@/shared/notifications/useTaskItemCounts';

// import types
import type {AppSwitcherMenuItem} from '@sdb/ui/types/components/molecules/AppSwitcherMenu.types';
import type {AcaAppElement} from '@/shared/types/AcaAppElement';
import type {NavigationRailMenuData} from '@sdb/ui/types/components/molecules/NavigationRail/NavigationRail.types';
import type {PropsWithChildren, RefObject} from 'react';
import type {Theme} from '@mui/material/styles';

initI18n();

interface TestMenuProps {
    litRef: RefObject<AcaAppElement | null>;
}

// Sub-component: Toast snackbar isolated so only toastStore changes trigger re-render
const ToastSnackbar = observer(() => {
    return (
        <Snackbar
            key={toastStore.currentToast?.timestamp}
            autoHideDuration={toastStore.currentToast?.duration}
            open={!!toastStore.currentToast}
            onClose={() => toastStore.clearToast()}
            anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
        >
            <Alert severity={toastStore.currentToast?.severity}>
                {toastStore.currentToast?.message}
            </Alert>
        </Snackbar>
    );
});

// Delayed AnnounceKit mounting isolated so LearningWrapper itself doesn't re-render after delay
const DelayedAnnounceKit = ({userId, hashedUserId}: {userId?: string; hashedUserId?: string}) => {
    const [show, setShow] = useState(false);
    useEffect(() => {
        if (!userId) return;
        const timer = setTimeout(() => setShow(true), 2000);
        return () => clearTimeout(timer);
    }, [userId]);
    if (!show) return null;
    return (
        <AnnounceKit
            user={{id: `lp_${hashedUserId}`}}
            data={{application: 'SDB Leerplatform'}}
            widget="https://announcekit.co/widgets/v2/2oPj8s"
        />
    );
};

// Observer component only for notifications & announcekit store dependent badge logic
const NotificationsTemplate = observer(
    (props: {
        children: React.ReactNode;
        t: ReturnType<typeof useTranslation>['t'];
        isVisible: boolean;
        userSettingsData: ReturnType<typeof useUserSettings>['data'];
        isUserSettingsLoading: boolean;
        navData: NavigationRailMenuData;
        navigate: (url: string) => void;
        selectedId: string | null;
        breadcrumbs: ReturnType<typeof useBreadcrumbs>;
        user: NonNullable<ReturnType<typeof useUserSettings>['data']>['user'] | undefined;
    }) => {
        const {
            t,
            isVisible,
            userSettingsData,
            isUserSettingsLoading,
            navData,
            navigate,
            selectedId,
            breadcrumbs,
            user,
            children,
        } = props;

        const minervaSettings = useMinervaSettings();
        const isAdmin = !!minervaSettings?.isAdmin;
        const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('expanded'));
        const hasNewReleaseNotes = announcekitStore.unreads > 0;
        const menuData = useMemo(
            () =>
                getWhatsNewMenuData(navData, {
                    isAdmin,
                    isSmallScreen,
                    hasNew: hasNewReleaseNotes,
                    label: t('whatsNew'),
                    onOpen: () => announcekitStore.open(),
                }),
            [isAdmin, isSmallScreen, navData, hasNewReleaseNotes, t],
        );

        const taskItemCounts = useTaskItemCounts(minervaSettings);

        const notifications = useMemo(() => {
            const unread = messageSummaryStore.unreadCount;
            const loading = messageSummaryStore.isLoading;
            const messages = messageSummaryStore.data?.firstUnreadMessages ?? [];
            const items = messages.map((x) => ({
                label: x.subject || '',
                payload: () =>
                    navigate(
                        `/message/inbox/detail/${encodeURIComponent(encodeURIComponent(x.id))}/message`,
                    ),
                badge: {color: 'warning' as const},
            }));

            const taskItems = getTaskItems(t, navigate, minervaSettings, taskItemCounts); // pass counts

            const totalTaskCount = Object.values(taskItemCounts).reduce(
                (sum: number, v: unknown) => sum + ((v as number) ?? 0),
                0,
            );

            return {
                badge: {badgeContent: unread + totalTaskCount, color: 'warning' as const},
                header: {title: t('notifications')},
                isLoading: loading,
                data: [
                    {
                        title: t('messages'),
                        noItemsLabel: t('noMessages'),
                        payload: () => navigate('/message/inbox/received'),
                        badge: {badgeContent: unread, color: 'warning' as const},
                        items,
                    },
                    {
                        title: t('tasks'),
                        payload: () => navigate('/tasks'),
                        badge: {badgeContent: totalTaskCount, color: 'warning' as const},
                        items: taskItems.items,
                    },
                ],
            };
        }, [
            t,
            navigate,
            messageSummaryStore.unreadCount,
            messageSummaryStore.isLoading,
            messageSummaryStore.data?.firstUnreadMessages?.length,
            minervaSettings,
            taskItemCounts,
        ]);
        return (
            <>
                <SdbDefaultTemplate
                    hideTemplate={!isVisible}
                    appSwitcher={{data: userSettingsData?.applications as AppSwitcherMenuItem[]}}
                    logo={{src: userSettingsData?.tenantLogoUrl ?? undefined}}
                    search={{
                        overwriteClick: () => {
                            navigate('/search');
                        },
                    }}
                    breadcrumbs={{data: breadcrumbs}}
                    profile={{
                        header: {
                            avatarProps: {src: user?.avatarUrl},
                            title: t('profile'),
                            email: user?.email,
                            emailProps: {linkProps: {href: 'mailto:' + user?.email}},
                            name: user?.displayName,
                        },
                        data: [
                            {
                                items: [
                                    {
                                        selected: false,
                                        type: 'card',
                                        label: t('myInformation'),
                                        icon: 'address-card',
                                        payload: () => navigate('/user/my/profile'),
                                    },
                                ],
                            },
                            {
                                title: 'SDB Leerplatform',
                                items: [
                                    {
                                        selected: false,
                                        type: 'card',
                                        label: t('help'),
                                        icon: 'question',
                                        payload: () =>
                                            window.open(
                                                'https://support.sdbgroep.nl/portal/nl/kb/sdb-leerplatform',
                                                '_blank',
                                            ),
                                    },
                                    {
                                        selected: false,
                                        type: 'card',
                                        label: t('releaseNotes'),
                                        icon: 'gift',
                                        badge:
                                            announcekitStore.unreads > 0
                                                ? {
                                                      color: 'error',
                                                      badgeContent: announcekitStore.unreads,
                                                  }
                                                : undefined,
                                        payload: () => announcekitStore.open(),
                                    },
                                    {
                                        selected: false,
                                        type: 'card',
                                        label: t('uservoiceIcon'),
                                        icon: 'wand-magic-sparkles',
                                        payload: () => window.open('/go-to-uservoice', '_blank'),
                                    },
                                    {
                                        selected: false,
                                        type: 'card',
                                        label: t('statusPage'),
                                        icon: 'gauge-high',
                                        payload: () =>
                                            window.open('https://status.sdbgroep.nl', '_blank'),
                                    },
                                    {
                                        type: 'card',
                                        label: t('restartFeatureDiscovery'),
                                        icon: 'signpost',
                                        dataTourId: 'FeatureDiscoverRestartButton',
                                        selected: false,
                                        payload: () => introFeatureDiscoveryStore.restart(),
                                    },
                                ],
                            },
                        ],
                        footer: {
                            withLove: true,
                            logoutButtonProps: {
                                children: t('signOut'),
                                onClick: () => store.dispatch({type: 'SIGNOUT'}),
                            },
                        },
                    }}
                    notifications={notifications}
                    navigationRail={{
                        withPinSwitch: false,
                        data: menuData as NavigationRailMenuData,
                        selectedId: selectedId ?? undefined,
                        isLoading: isUserSettingsLoading,
                        ...getWhatsNewRailProps({isAdmin, label: t('whatsNew')}),
                    }}
                >
                    {children}
                </SdbDefaultTemplate>
            </>
        );
    },
);

const LearningWrapper = ({children, litRef}: PropsWithChildren<TestMenuProps>) => {
    const {t} = useTranslation();

    useInitializeTheme();

    const {isVisible} = useAcaAppLitElement(litRef);
    const {isLoading: isUserSettingsLoading, data: userSettingsData} = useUserSettings();

    const user = userSettingsData?.user;
    const settingsTenantId = userSettingsData?.tenantId;

    const {navData, navigate, selectedId} = useNavigation(userSettingsData?.menu);
    const breadcrumbs = useBreadcrumbs(navData, selectedId ?? undefined);

    const {userId, tenantId, setUserId, setTenantId} = useEnvironmentContext();

    // Narrow dependencies so we don't re-run purely because we set the values
    useEffect(() => {
        if (user?.idString && userId !== user.idString) {
            setUserId(user.idString);
        }
        if (settingsTenantId && tenantId !== settingsTenantId) {
            setTenantId(settingsTenantId);
        }
    }, [user?.idString, settingsTenantId]);

    useEffect(() => {
        if (user) {
            messageSummaryStore.startPolling();
        }
        return () => messageSummaryStore.stopPolling();
    }, [user]);

    // Greet users with the intro tour (lp-intro-tour-2026-09) on their first visit. Use restart()
    // rather than relying on the store's auto-open: restart() sets `viewed = false`, which stops the
    // tour organism from syncing with the server and silently marking it viewed without ever showing
    // it. Gated on `!viewed` so anyone who already completed it isn't shown it again.
    useEffect(() => {
        if (!user || introFeatureDiscoveryStore.viewed) return;
        introFeatureDiscoveryStore.restart();
    }, [user]);

    // Memoized hashed user id (E)
    const hashedUserId = useSwr(user?.idString ? user.idString : null, () =>
        hashStringUtil(user?.idString ?? ''),
    );

    return (
        <>
            <ThemeProvider>
                <NotificationsTemplate
                    t={t}
                    isVisible={isVisible}
                    userSettingsData={userSettingsData}
                    isUserSettingsLoading={isUserSettingsLoading}
                    navData={navData}
                    navigate={navigate}
                    selectedId={selectedId}
                    breadcrumbs={breadcrumbs}
                    user={user}
                >
                    {children}
                </NotificationsTemplate>
                <DashboardFeatureDiscovery />
                <NpsClient
                    labels={{
                        cancel: t('npsDialog.cancel'),
                        confirm: t('npsDialog.confirm'),
                        likely: t('npsDialog.likely'),
                        no: t('npsDialog.no'),
                        notLikely: t('npsDialog.notLikely'),
                        remarks: t('npsDialog.remarks'),
                        submit: t('npsDialog.submit'),
                        title: t('npsDialog.title'),
                        yes: t('npsDialog.yes'),
                        subTitle: t('npsDialog.subTitle'),
                        later: t('npsDialog.later'),
                        thanks: t('npsDialog.thanks'),
                        improve: t('npsDialog.improve'),
                        close: t('npsDialog.close'),
                    }}
                />
            </ThemeProvider>
            <UserDetailsSync userId={user?.idString} />
            <DelayedAnnounceKit
                userId={user?.idString}
                hashedUserId={hashedUserId.data}
            />
            <ToastSnackbar />
        </>
    );
};

export default LearningWrapper;
